import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {IThemeVariables} from '@toolify/client/src/modules/theme/types/IThemeVariables'
import {TextCanvasTableElementAlignType} from '@toolify/client/src/modules/tables/strategies/CanvasTableElementStrategies/TextCanvasTableElementStrategy/enum/TextCanvasTableElementAlignType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {TimeTrackerDropdownLayoutType} from '@toolify/client/src/modules/databases/enum/TimeTrackerDropdownLayoutType'
import {ChoosePropertyTypeLayoutType} from '@toolify/client/src/modules/databases/enum/ChoosePropertyTypeLayoutType'
import {StatusDropdownLayoutType} from '@toolify/client/src/modules/databases/enum/StatusDropdownLayoutType'
import {ItemGroupHeaderLayoutType} from '@toolify/client/src/modules/ui/enum/ItemGroupHeaderLayoutType'
import {formatHex} from 'culori'

const lightnessAndHueOffsetValues = [
  [0.64, 0.16],
  [0.58, 0.14],
  [0.52, 0.12],
  [0.46, 0.10],
]

const hueArray = [240, 270, 300, 330, 0, 30, 60, 90, 120, 150, 180, 210]

const PaletteColors: string[] = lightnessAndHueOffsetValues.map(([lightness, hueOffset]) => {
  return [
    formatHex({
      mode: 'oklch',
      l: lightness,
      c: 0.01,
      h: 245,
      alpha: 1,
    }),
  ].concat(hueArray.map((hue) => {
    return formatHex({
      mode: 'oklch',
      l: lightness,
      c: hueOffset,
      h: hue,
      alpha: 1,
    })
  }))
}).flat()

const PaletteColorMatrix = lightnessAndHueOffsetValues.map((_, y) => {
  return (new Array(hueArray.length + 1)).fill(null).map((_, x) => {
    return (y * (hueArray.length + 1)) + x
  })
})

export const LightThemeVariables: IThemeVariables = {
  [ThemeVariableType.PrimaryColor]: 'oklch(65% 0.1683 255)',
  [ThemeVariableType.PrimaryHoverColor]: 'oklch(70% 0.1683 255)',
  [ThemeVariableType.OnPrimaryColor]: '#d1dff6',
  [ThemeVariableType.OnPrimaryHoverColor]: '#d1dff6',
  [ThemeVariableType.PrimaryContainerColor]: 'oklch(95% 0.005 245)',
  [ThemeVariableType.PrimaryContainerHoverColor]: 'oklch(97% 0.005 245)',
  [ThemeVariableType.OnPrimaryContainerColor]: 'oklch(30% 0.01 245)',

  [ThemeVariableType.SecondaryColor]: '#677388',
  [ThemeVariableType.OnSecondaryColor]: '#cdd2db',
  [ThemeVariableType.SecondaryContainerColor]: '#404b5e',
  [ThemeVariableType.OnSecondaryContainerColor]: '#e8eaed',

  [ThemeVariableType.TertiaryColor]: '#9200c4',
  [ThemeVariableType.OnTertiaryColor]: '#e4b5ff',
  [ThemeVariableType.TertiaryContainerColor]: '#580079',
  [ThemeVariableType.OnTertiaryContainerColor]: '#edd3fd',

  [ThemeVariableType.ErrorColor]: 'oklch(65% 0.1683 31)',
  [ThemeVariableType.OnErrorColor]: 'oklch(95% 0.01 31)',
  [ThemeVariableType.ErrorContainerColor]: '#93000a',
  [ThemeVariableType.OnErrorContainerColor]: '#ffdad6',

  [ThemeVariableType.SurfaceDimColor]: 'oklch(87% 0.005 245)',
  [ThemeVariableType.SurfaceColor]: 'oklch(91% 0.005 245)',
  [ThemeVariableType.SurfaceBrightColor]: '#f2f5f8',

  [ThemeVariableType.ContainerHighestColor]: 'oklch(100% 0.005 245)',
  [ThemeVariableType.ContainerHighColor]: 'oklch(99% 0.005 245)',
  [ThemeVariableType.ContainerColor]: 'oklch(97% 0.005 245)',
  [ThemeVariableType.ContainerLowColor]: 'oklch(95% 0.005 245)',
  [ThemeVariableType.ContainerLowestColor]: 'oklch(93% 0.005 245)',

  [ThemeVariableType.ContentBrightColor]: 'oklch(30% 0.01 245)',
  [ThemeVariableType.ContentLightColor]: 'oklch(30% 0.01 245)',
  [ThemeVariableType.ContentNormalColor]: 'oklch(30% 0.01 245)',
  [ThemeVariableType.ContentDimColor]: 'oklch(55% 0.01 245)',
  [ThemeVariableType.ContentDarkColor]: 'oklch(75% 0.01 245)',
  [ThemeVariableType.ContentDarkestColor]: 'oklch(85% 0.01 245)',

  [ThemeVariableType.BorderRadius]: 8,
  [ThemeVariableType.Shadow]: '#56638026',
  [ThemeVariableType.MobileFontSizeFactor]: 1,

  [ThemeVariableType.DatabaseTableHeaderDefaultBackgroundColor]: 'oklch(95% 0.005 245)',
  [ThemeVariableType.DatabaseTableHeaderHoverBackgroundColor]: 'oklch(97% 0.005 245)',
  [ThemeVariableType.DatabaseCellHoverBackgroundColor]: 'oklch(99% 0.005 245)',
  [ThemeVariableType.DatabaseCellHoverBorder]: 'none',
  [ThemeVariableType.DatabaseCellFocusHoverBorder]: 'none',
  [ThemeVariableType.DatabaseRelationIconBackgroundColor]: 'transparent',
  [ThemeVariableType.DatabaseRelationIconBackgroundHoverColor]: 'transparent',
  [ThemeVariableType.DatabaseTableColumnNameAlignment]: TextCanvasTableElementAlignType.Left,
  [ThemeVariableType.FileIconWeightType]: IconWeightType.Solid,
  [ThemeVariableType.NotificationIconWeightType]: IconWeightType.Solid,
  [ThemeVariableType.PreferredIconWeightType]: IconWeightType.Solid,
  [ThemeVariableType.DatabaseHeaderGroupDropdownPlacement]: 'bottom-end',
  [ThemeVariableType.DashboardRecentListBackgroundColor]: 'transparent',
  [ThemeVariableType.DashboardRecentListHorizontalPadding]: '0',
  [ThemeVariableType.ShowDashboardRecentItemBorders]: false,
  [ThemeVariableType.UseStickyDashboardResourcesButton]: true,
  [ThemeVariableType.ShouldShowDashboardAndKanbanInContainer]: true,
  [ThemeVariableType.UseDetachedDashboardWidgetTypePreview]: true,
  [ThemeVariableType.CanCreateConversationDashboardWidget]: true,

  [ThemeVariableType.UseScrollbarGradients]: true,
  [ThemeVariableType.UseModernAuthLayout]: true,
  [ThemeVariableType.PreferBorders]: false,
  [ThemeVariableType.UseUnifiedMarkdownEditorBackground]: true,
  [ThemeVariableType.ShouldUseLineStyleInFilters]: true,
  [ThemeVariableType.ShowNewMessageButtonOutsideInput]: false,
  [ThemeVariableType.ShowSidebarWorkspaceDropdownToggle]: false,
  [ThemeVariableType.ShowSidebarFavouriteIconChips]: true,
  [ThemeVariableType.ShowCloudItemDetails]: false,
  [ThemeVariableType.ShowCloudItemIcon]: true,
  [ThemeVariableType.ShowEmptyFilesPlaceholderVisual]: true,
  [ThemeVariableType.ShowEmptyConversationPlaceholderVisual]: true,
  [ThemeVariableType.ShowWorkspaceInviteAsButton]: true,
  [ThemeVariableType.ShouldShowModalHeaderBackgrounds]: false,
  [ThemeVariableType.ShouldShowBreadcrumbsTitleFirst]: false,
  [ThemeVariableType.UsePaletteColorsInAvatars]: true,
  [ThemeVariableType.ShouldClampNotificationTitleItem]: false,
  [ThemeVariableType.ShowNotificationResourceSpace]: false,
  [ThemeVariableType.UseCenteredTimeTrackerCellLayout]: false,
  [ThemeVariableType.TimeTrackerDropdownLayoutType]: TimeTrackerDropdownLayoutType.Modern,
  [ThemeVariableType.ChoosePropertyTypeLayoutType]: ChoosePropertyTypeLayoutType.Modern,
  [ThemeVariableType.StatusDropdownLayoutType]: StatusDropdownLayoutType.Modern,
  [ThemeVariableType.ItemGroupHeaderLayoutType]: ItemGroupHeaderLayoutType.Modern,

  [ThemeVariableType.PaletteColors]: PaletteColors,
  [ThemeVariableType.PaletteColorMatrix]: PaletteColorMatrix,
}
